import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Chip, Tooltip } from "@mui/material";
import React from "react";

export type RokuStatus = "synced" | "require_sync" | "not_exist" | null;

export const ROKU_STATUS_LABELS: Record<Exclude<RokuStatus, null>, string> = {
    synced: "Synced",
    require_sync: "Out of Sync",
    not_exist: "Not in Roku",
};

export const ROKU_STATUS_TOOLTIPS: Record<Exclude<RokuStatus, null>, string> = {
    synced: "Catalog matches the Roku EMA Avails file last submitted to Roku",
    require_sync:
        "Submitted to Roku, but the catalog has changed since (dates, branding, format, …) — submit again",
    not_exist: "Not yet submitted to Roku",
};

function rokuChipColor(
    status: Exclude<RokuStatus, null>
): "success" | "warning" | "error" {
    if (status === "synced") return "success";
    if (status === "require_sync") return "warning";
    return "error";
}

export default function RokuIndicator({
    status,
    showLabel = true,
}: {
    status: RokuStatus;
    showLabel?: boolean;
}) {
    if (status == null) {
        return (
            <Tooltip title="Roku status pending">
                <HelpOutlineIcon fontSize="small" color="disabled" />
            </Tooltip>
        );
    }

    const label = ROKU_STATUS_LABELS[status];
    const icon =
        status === "synced" ? (
            <CheckCircleIcon color="success" fontSize="small" />
        ) : status === "require_sync" ? (
            <WarningAmberIcon color="warning" fontSize="small" />
        ) : (
            <ErrorIcon color="error" fontSize="small" />
        );

    return (
        <Tooltip title={ROKU_STATUS_TOOLTIPS[status]}>
            <Chip
                size="small"
                color={rokuChipColor(status)}
                variant="outlined"
                icon={icon}
                label={showLabel ? label : undefined}
                sx={{ maxWidth: "100%" }}
            />
        </Tooltip>
    );
}
